import type {EnvNameV3} from '@smart/endpoint-directory';
import type {LocaleV3} from '@smart/locale-service';
import {getCountryCode, getCurrency, getLanguage} from '@smart/locale-service';
import type {LoginStateV3} from '@smart/login-service';
import type {
  FinanceParamsV1,
  LeasingParamsV1,
} from '@smart/payment-config-service';
import type {PriceTypeV1} from '@smart/price-preferences-provider';
import type {PriceDetailsByType} from '../../bff/price-details';
import type {DataLayerEvent} from './push-to-data-layer';
import {pushToDataLayer} from './push-to-data-layer';

export interface TrackApplyOptions {
  readonly envName: EnvNameV3;
  readonly mode: 'cash-buy' | 'leasing' | 'finance';
  readonly params: FinanceParamsV1 | LeasingParamsV1;
  readonly locale: LocaleV3;
  readonly loginState: LoginStateV3 | undefined;
  readonly priceType: PriceTypeV1;
  readonly priceDetails: PriceDetailsByType | undefined;
}

export function trackApply(options: TrackApplyOptions): void {
  const {envName, mode, params, locale, loginState, priceType, priceDetails} =
    options;

  const isLoggedIn = loginState?.status === `logged-in`;
  const financeInfo: Record<
    TrackApplyOptions['mode'],
    () => DataLayerEvent['eventInfo']['financeInfo']
  > = {
    'cash-buy': () => ({
      type: priceType,
      paymentMethod: mode as 'cash-buy',
      value: priceDetails?.[priceType].price?.value.toString(),
    }),
    'leasing': () => ({
      type: priceType,
      paymentMethod: mode as 'leasing',
      downpayment:
        `advancePayment` in params
          ? params.advancePayment.toString()
          : params.advanceMonth.toString(),
      duration: params.duration.toString(),
      mileage: params.mileage.toString(),
      value: priceDetails?.[priceType].monthFee?.value.toString(),
    }),
    'finance': () => ({
      type: priceType,
      paymentMethod: mode as 'leasing',
      downpayment:
        `advancePayment` in params
          ? params.advancePayment.toString()
          : params.advanceMonth.toString(),
      duration: params.duration.toString(),
      mileage: params.mileage.toString(),
      value: priceDetails?.[priceType].monthFee?.value.toString(),
    }),
  };

  pushToDataLayer({
    event: `click`,

    eventInfo: {
      type: `feature`,
      region: `finance_calculator`,
      name: `submit`,
      financeInfo: financeInfo[mode](),
    },

    user: loginState
      ? {
          loginStatus: `logged_in`,
          customerId:
            isLoggedIn && loginState.customerType ? loginState.uid : ``,
          type:
            isLoggedIn && loginState.customerType === `private` ? `b2c` : `b2b`,
        }
      : {loginStatus: `logged_out`},

    application: {
      id: `fincalc`,
      version: process.env.APP_VERSION!,
      env: envName,
      language: getLanguage(locale.languageTag),
      market: getCountryCode(locale.marketId).toLowerCase(),
      currency: getCurrency(locale.marketId),
    },
  });
}
